import React from 'react';
import PropTypes from 'prop-types';
import ResilienceSection from './ResilienceSection';
import PersistenceSection from './PersistenceSection';
import ChallengeSeekingSection from './ChallengeSeekingSection';
import ProblemSolvingSection from './ProblemSolvingSection';
import GoalSettingSection from './GoalSettingSection';
import DivergentThinkingConstructSection from './DivergentThinkingConstructSection';
import ProbabilitySection from './probabilitySection';
import BehaviorsSection from './behaviorsSection';
import { PassagesDisplay } from './passages';

/**
 * ConstructRenderer - Handles conditional rendering of different construct sections
 *
 * This component acts as a router for different construct types, rendering the appropriate
 * UI components based on the current construct. For Resilience, it renders a specialized
 * ResilienceSection with quadrant visualization. For Persistence, it renders a specialized
 * PersistenceSection with normal distribution (bell curve) visualization. For Challenge Seeking,
 * it renders a specialized ChallengeSeekingSection with clustering-based bar chart and behavioral
 * insights. For Problem Solving, it renders a specialized ProblemSolvingSection with personalized
 * passages and behavioral insights. For Divergent Thinking, it renders a specialized
 * DivergentThinkingConstructSection with normal distribution visualization and personalized content.
 * For other constructs, it renders the standard combination of ProbabilitySection, BehaviorsSection,
 * and PassagesDisplay.
 *
 * @param {Object} props - Component props
 * @param {string} props.currentConstruct - The current construct type (e.g., 'resilience', 'persistence', 'challenge seeking', 'problem solving', 'divergent thinking')
 * @param {Object} props.contextData - The context data from Redux store containing construct and behavior data
 * @param {Object} props.descriptions - Chart descriptions for the current construct
 * @param {Object} props.constructDescriptions - Construct-specific descriptions for behaviors and impact
 * @param {boolean} props.isLoading - Loading state indicator (currently unused but reserved for future use)
 * @returns {JSX.Element} The appropriate section component based on construct type
 */
const ConstructRenderer = ({
  currentConstruct,
  contextData,
  descriptions,
  constructDescriptions,
  isLoading
}) => {
  // Special handling for Resilience construct
  if (currentConstruct === "resilience") {
    return <ResilienceSection />;
  }

  // Special handling for Persistence construct
  if (currentConstruct === "persistence") {
    return <PersistenceSection />;
  }

  // Special handling for Challenge Seeking construct
  if (currentConstruct === "challenge seeking" || currentConstruct === "challenge-seeking" || currentConstruct === "challengeseeking") {
    return <ChallengeSeekingSection />;
  }

  // Special handling for Problem Solving construct
  if (currentConstruct === "problem solving" || currentConstruct === "problem-solving" || currentConstruct === "problemsolving") {
    return <ProblemSolvingSection />;
  }

  // Special handling for Goal Setting construct
  if (currentConstruct === "goal setting" || currentConstruct === "goal-setting" || currentConstruct === "goalsetting") {
    return <GoalSettingSection />;
  }

  // Special handling for Divergent Thinking construct
  if (currentConstruct === "divergent thinking" || currentConstruct === "divergent-thinking" || currentConstruct === "divergentthinking") {
    // Only render if we have valid contextData or if it's explicitly null (which the component can handle)
    return <DivergentThinkingConstructSection contextData={contextData} />;
  }

  // Default rendering for other constructs
  return (
    <div className="flex flex-col gap-8">
      {/* Probability Section with attached explanation */}
      <div className="flex flex-col md:flex-row">
        <div className="w-full md:w-1/2 bg-blue-50 rounded-l-lg overflow-hidden">
          <ProbabilitySection />
        </div>
        <div className="w-full md:w-1/2 bg-white p-6 rounded-r-lg border-t border-r border-b border-gray-200 shadow-sm">
          <div className="flex items-center mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-lg font-semibold text-black">Understanding Probability</h3>
          </div>

          <div className="mb-4 pl-2 border-l-4 border-blue-200">
            <p className="text-gray-700">
              {descriptions?.probabilityChart}
            </p>
          </div>
        </div>
      </div>

      {/* Behaviors Section with attached explanation */}
      <div className="flex flex-col md:flex-row">
        <div className="w-full md:w-1/2 bg-blue-50 rounded-l-lg overflow-hidden">
          <BehaviorsSection />
        </div>
        <div className="w-full md:w-1/2 bg-white p-6 rounded-r-lg border-t border-r border-b border-gray-200 shadow-sm">
          <div className="flex items-center mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <h3 className="text-lg font-semibold text-black">Understanding Behaviors</h3>
          </div>

          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">What This Chart Shows:</h4>
            <p className="text-gray-700 text-sm pl-2 border-l-2 border-blue-100">
              This chart compares your gameplay behaviors to a nationally representative sample.
              Each row represents a key behavior, with a marker indicating your percentile rank.
            </p>
          </div>

          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Selected Behaviors:</h4>
            <p className="text-gray-700 text-sm pl-2 border-l-2 border-blue-100">
              The displayed behaviors are carefully selected to reflect {currentConstruct?.toLowerCase()}-related actions
              {constructDescriptions?.behaviors}
            </p>
          </div>

          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Important Note:</h4>
            <p className="text-gray-700 text-sm pl-2 border-l-2 border-blue-100">
              While the percentile rank provides insight into performance relative to others, it only represents a subset of behaviors—many additional factors contribute to the overall model.
            </p>
          </div>

          <div className="mt-4 bg-blue-50 p-3 rounded-md flex items-start">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <p className="text-gray-700 text-sm">
              Because {currentConstruct?.toLowerCase()}
              {' '}{constructDescriptions?.impact}{' '}
              improving a single behavior may not significantly change overall results, but{' '}
              {constructDescriptions?.improvement}{' '}
              will be reflected in the assessment.
            </p>
          </div>
        </div>
      </div>

      {/* Passages Section for non-resilience constructs */}
      <div className="flex flex-col md:flex-row">
        <div className="w-full md:w-1/2 bg-purple-50 rounded-l-lg overflow-hidden">
          <PassagesDisplay
            contextData={contextData}
          />
        </div>
        <div className="w-full md:w-1/2 bg-white p-6 rounded-r-lg border-t border-r border-b border-gray-200 shadow-sm">
          <div className="flex items-center mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-lg font-semibold text-black">Understanding Performance Passages</h3>
          </div>

          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">What This Section Shows:</h4>
            <p className="text-gray-700 text-sm pl-2 border-l-2 border-purple-100">
              This section provides detailed insights into your performance across different constructs and game types.
              Each construct displays personalized passage text based on your specific performance levels.
            </p>
          </div>

          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Personalized Content:</h4>
            <p className="text-gray-700 text-sm pl-2 border-l-2 border-purple-100">
              The passages are automatically selected based on your percentile rankings. You'll see different content
              for low (0-33%), middle (34-66%), or high (67-100%) performance levels.
            </p>
          </div>

          <div className="mt-4 bg-purple-50 p-3 rounded-md flex items-start">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <p className="text-gray-700 text-sm">
              The passages are dynamically generated based on your actual gameplay performance data
              and percentile rankings from the assessment results.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// PropTypes for type checking
ConstructRenderer.propTypes = {
  currentConstruct: PropTypes.string.isRequired,
  contextData: PropTypes.object,
  descriptions: PropTypes.object,
  constructDescriptions: PropTypes.object,
  isLoading: PropTypes.bool
};

// Default props
ConstructRenderer.defaultProps = {
  contextData: null,
  descriptions: {},
  constructDescriptions: {},
  isLoading: false
};

export default ConstructRenderer;
